// src/pages/Accounting/Receipts/components/EmptyState.tsx

import React from "react";
import {
  PALETTE,
  SUPPORTED_FORMATS_LABEL,
  MAX_FILE_SIZE_MB,
} from "../constants";
import { BenefitsRow } from "./BenefitsRow";
import { HeroDropZone } from "./HeroDropZone";
import { ReadyToUseEmail } from "./ReadyToUseEmail";
import { OptionalConnections } from "./OptionalConnections";

interface EmptyStateProps {
  userEmail: string;
  onFilesDropped: (files: File[]) => void;
  onEnterManually: () => void;
  onGetMobileApp: () => void;
  onConnectInbox: () => void;
}

export function EmptyState({
  userEmail,
  onFilesDropped,
  onEnterManually,
  onGetMobileApp,
  onConnectInbox,
}: EmptyStateProps) {
  return (
    <div>
      {/* Opening lead with inline Vela mention (standing pattern: no big Vela banner on empty states) */}
      <p
        style={{
          fontSize: 14,
          color: PALETTE.ink2,
          lineHeight: 1.6,
          margin: "0 0 28px",
          fontWeight: 500,
          maxWidth: 720,
        }}
      >
        Every receipt you add is read, extracted, and matched to a bank
        transaction automatically by{" "}
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            background: "linear-gradient(135deg, #7C3AED 0%, #6B46C1 100%)",
            color: "#fff",
            borderRadius: "50%",
            width: 18,
            height: 18,
            fontSize: 10,
            fontWeight: 800,
            verticalAlign: "-3px",
            margin: "0 2px",
          }}
        >
          V
        </span>{" "}
        <strong style={{ color: PALETTE.ink, fontWeight: 700 }}>Vela</strong>.
        You review only what Vela isn't sure about.
      </p>

      <BenefitsRow />

      <div
        style={{
          fontSize: 11,
          fontWeight: 800,
          letterSpacing: "0.12em",
          color: PALETTE.ink2,
          textTransform: "uppercase",
          margin: "0 0 14px",
        }}
      >
        Add your first receipt
      </div>

      <HeroDropZone onFilesDropped={onFilesDropped} />

      <ReadyToUseEmail userEmail={userEmail} />

      <OptionalConnections
        onGetMobileApp={onGetMobileApp}
        onConnectInbox={onConnectInbox}
      />

      {/* Fallback: enter manually */}
      <div
        style={{
          maxWidth: 720,
          paddingTop: 18,
          borderTop: `1px solid ${PALETTE.line}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontSize: 12.5,
          color: PALETTE.ink3,
        }}
      >
        <div>
          <strong style={{ color: PALETTE.ink, fontWeight: 700 }}>
            Have a receipt but no scan?
          </strong>{" "}
          Enter it by hand (vendor, date, amount).
        </div>
        <button
          type="button"
          onClick={onEnterManually}
          style={{
            height: 34,
            padding: "0 14px",
            borderRadius: 9,
            border: `1px solid ${PALETTE.line}`,
            background: "#fff",
            color: PALETTE.ink,
            fontSize: 12,
            fontWeight: 700,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          + Enter manually
        </button>
      </div>

      <div
        style={{
          fontSize: 11,
          color: PALETTE.ink4,
          maxWidth: 720,
          marginTop: 14,
        }}
      >
        {SUPPORTED_FORMATS_LABEL} supported. {MAX_FILE_SIZE_MB} MB per file max.
      </div>
    </div>
  );
}
